/**
 * AUA HYDRATE — Sections behaviors (consolidated)
 *
 * Bundles all per-section client logic into a single deferred external file.
 * Loaded with `defer` from theme.liquid, runs after HTML parsing, so each
 * IIFE just queries the DOM (returns early if not present on the page).
 *
 * Source sections (inlined here for perf — single HTTP request, cacheable):
 *  - header        : scroll state + mobile menu toggle + a11y
 *  - hero-manifesto: desktop scroll-jacking (expand effect)
 *  - footer        : curtain wordmark fade on scroll to bottom
 *  - social-feed   : horizontal carousel arrows
 */

// ============================================================
// HEADER — scroll opacity + mobile menu
// ============================================================
(() => {
  const header = document.querySelector('[data-aua-header]');
  if (!header) return;

  const onScroll = () => {
    if (window.scrollY > 12) header.setAttribute('data-scrolled', '');
    else header.removeAttribute('data-scrolled');
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const toggle = header.querySelector('[data-menu-toggle]');
  const menu = header.querySelector('[data-mobile-menu]');
  if (!toggle || !menu) return;

  const setOpen = (open) => {
    header.toggleAttribute('data-menu-open', open);
    menu.toggleAttribute('data-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? 'Fermer le menu' : 'Ouvrir le menu');
    document.body.classList.toggle('aua-menu-locked', open);
  };

  toggle.addEventListener('click', () => setOpen(!menu.hasAttribute('data-open')));

  menu.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => setOpen(false));
  });

  menu.addEventListener('click', (e) => {
    if (e.target === menu) setOpen(false);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.hasAttribute('data-open')) setOpen(false);
  });

  const mq = window.matchMedia('(min-width: 769px)');
  const onChange = (e) => { if (e.matches) setOpen(false); };
  mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
})();


// ============================================================
// HERO-MANIFESTO — desktop scroll-jacking (expand effect)
// ============================================================
(() => {
  const hero = document.querySelector('[data-scroll-hero]');
  if (!hero) return;

  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (isMobile || reduceMotion) {
    hero.style.setProperty('--scroll-progress', '1');
    hero.setAttribute('data-content-shown', '');
    return;
  }

  const state = {
    progress: 0,
    expanded: false,
    touchStartY: 0,
    pendingDelta: 0,
    rafId: null,
  };

  const WHEEL_SENS = 0.0011;
  const TOUCH_SENS_DOWN = 0.0065;
  const TOUCH_SENS_UP = 0.0095;

  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  const flushUpdate = () => {
    state.rafId = null;
    if (state.pendingDelta === 0) return;
    state.progress = clamp(state.progress + state.pendingDelta, 0, 1);
    state.pendingDelta = 0;
    hero.style.setProperty('--scroll-progress', state.progress);

    if (state.progress >= 1 && !state.expanded) {
      state.expanded = true;
      hero.setAttribute('data-content-shown', '');
      document.body.removeAttribute('data-aua-scroll-locked');
    } else if (state.progress < 0.75 && state.expanded) {
      state.expanded = false;
      hero.removeAttribute('data-content-shown');
      if (window.scrollY <= 5) {
        window.scrollTo(0, 0);
        document.body.setAttribute('data-aua-scroll-locked', '');
      }
    }
  };

  const queueDelta = (delta) => {
    state.pendingDelta += delta;
    if (state.rafId === null) {
      state.rafId = requestAnimationFrame(flushUpdate);
    }
  };

  const handleWheel = (e) => {
    if (state.expanded && e.deltaY < 0 && window.scrollY <= 5) {
      e.preventDefault();
      queueDelta(e.deltaY * WHEEL_SENS);
      return;
    }
    if (!state.expanded) {
      e.preventDefault();
      queueDelta(e.deltaY * WHEEL_SENS);
    }
  };

  const handleTouchStart = (e) => {
    state.touchStartY = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!state.touchStartY) return;
    const touchY = e.touches[0].clientY;
    const deltaY = state.touchStartY - touchY;

    if (state.expanded && deltaY < -20 && window.scrollY <= 5) {
      e.preventDefault();
      queueDelta(deltaY * TOUCH_SENS_UP);
      state.touchStartY = touchY;
      return;
    }
    if (!state.expanded) {
      e.preventDefault();
      const sens = deltaY > 0 ? TOUCH_SENS_DOWN : TOUCH_SENS_UP;
      queueDelta(deltaY * sens);
      state.touchStartY = touchY;
    }
  };

  const handleTouchEnd = () => {
    state.touchStartY = 0;
  };

  document.body.setAttribute('data-aua-scroll-locked', '');

  window.addEventListener('wheel', handleWheel, { passive: false });
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchmove', handleTouchMove, { passive: false });
  window.addEventListener('touchend', handleTouchEnd, { passive: true });

  window.addEventListener('beforeunload', () => {
    document.body.removeAttribute('data-aua-scroll-locked');
  });
})();


// ============================================================
// FOOTER — curtain wordmark fade on scroll to bottom
// ============================================================
(() => {
  const wordmark = document.querySelector('[data-footer-wordmark]');
  if (!wordmark) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    wordmark.style.opacity = '1';
    wordmark.style.transform = 'none';
    return;
  }

  let ticking = false;
  const update = () => {
    ticking = false;
    const doc = document.documentElement;
    const distanceFromBottom = doc.scrollHeight - (window.scrollY + window.innerHeight);
    const span = Math.max(360, window.innerHeight * 0.5);
    const p = Math.min(1, Math.max(0, 1 - distanceFromBottom / span));
    wordmark.style.opacity = p.toFixed(3);
    wordmark.style.transform = 'translateY(' + ((1 - p) * 40).toFixed(1) + 'px)';
  };
  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();


// ============================================================
// SOCIAL FEED — carousel arrows
// ============================================================
(() => {
  const root = document.querySelector('[data-social-feed]');
  if (!root) return;
  const track = root.querySelector('[data-social-track]');
  const prev = root.querySelector('[data-social-prev]');
  const next = root.querySelector('[data-social-next]');
  if (!track) return;

  const stepDistance = () => {
    const card = track.querySelector('.aua-social__card');
    if (!card) return 320;
    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
    return card.getBoundingClientRect().width + gap;
  };

  if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -stepDistance(), behavior: 'smooth' }));
  if (next) next.addEventListener('click', () => track.scrollBy({ left: stepDistance(), behavior: 'smooth' }));
})();
